import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
const base = `${apiBase}/api/macro`;

export interface MacroDatasetReport {
	id: string;
	name: string;
	seriesCount: number;
	periodCount: number;
	observationCount: number;
	missingCellCount: number;
	firstPeriod?: string | null;
	lastPeriod?: string | null;
}

export interface MacroImportReport {
	batchId: string;
	fileName: string;
	sha256: string;
	sourceAsOfUtc?: string | null;
	datasetCount: number;
	seriesCount: number;
	periodCount: number;
	observationCount: number;
	missingCellCount: number;
	duplicatePeriodCount: number;
	invalidPeriodCount: number;
	isValid: boolean;
	alreadyCommitted: boolean;
	datasets: MacroDatasetReport[];
	errors: string[];
	warnings: string[];
	/** Indicateurs de ce classeur inconnus de la base actuelle (« Sujet : clé »). */
	seriesAdded?: string[];
	/** Indicateurs de la base actuelle absents de ce classeur : ils disparaîtraient au commit. */
	seriesRemoved?: string[];
	/** Sujets dont les données reculent (dernière période plus ancienne, observations en baisse). */
	periodRegressions?: string[];
	/** Empreinte de `periodRegressions` : la confirmation du retrait la renvoie pour porter sur CETTE liste. */
	periodRegressionsFingerprint?: string;
	/** Trace d'un retrait de données confirmé au commit (qui, quand, quels reculs). */
	dataRemoval?: MacroDataRemovalRecord | null;
	/** Onglets rattachés à un sujet autrement que par leur nom exact, avec les deux noms réels. */
	sheetsResolved?: MacroSheetMatch[];
	/** Onglets du classeur qu'aucun sujet ne réclame : ignorés, jamais silencieusement. */
	sheetsUnknown?: string[];
	/** Sujets attendus dont l'onglet est introuvable. */
	subjectsMissing?: string[];
	/** Sujets encore déclarés dont l'onglet manque : ils GARDENT leurs données au lieu d'être effacés. */
	subjectsFrozen?: string[];
	/** La forme du classeur s'écarte de la forme attendue. */
	formatChanged?: boolean;
}

/** Un retrait de données publiées, confirmé explicitement au moment du commit. */
export interface MacroDataRemovalRecord {
	acceptedBy?: string | null;
	acceptedAtUtc: string;
	items: string[];
}

/** Un onglet reconnu sous un autre nom que son nom canonique. */
export interface MacroSheetMatch {
	sheetName: string;
	datasetId: string;
	datasetName: string;
	canonicalSheet: string;
	/** `alias`, `normalized` ou `truncated`. */
	kind: string;
	explanation: string;
}

/**
 * Écart de forme PERSISTANT. À la différence d'un avertissement de rapport, qui ne vaut que pour
 * un import, celui-ci reste ouvert tant que sa cause n'a pas disparu : un second classeur au même
 * défaut ne le referme pas, il ne fait que rafraîchir `lastSeenAtUtc`.
 */
export interface MacroStructureAnomaly {
	id: number;
	kind: "sheet-renamed" | "sheet-unknown" | "subject-missing" | "series-unconfigured" | "column-unknown" | string;
	subject: string;
	datasetId?: string | null;
	detail: string;
	detectedAtUtc: string;
	lastSeenAtUtc: string;
	resolvedAtUtc?: string | null;
	resolvedBy?: string | null;
}

export interface MacroImportHistoryItem {
	id: string;
	fileName: string;
	sha256: string;
	status: string;
	isAuthoritative: boolean;
	sourceAsOfUtc?: string | null;
	createdBy?: string | null;
	createdAtUtc: string;
	committedAtUtc?: string | null;
	/** False quand le blob du classeur a été purgé par la rétention (métadonnées conservées). */
	hasWorkbook: boolean;
	report?: MacroImportReport | null;
}

export interface MacroImportCommitResult {
	batchId: string;
	datasetCount: number;
	seriesCount: number;
	observationCount: number;
	previewPublished: boolean;
	savedRecordIds: string[];
	skippedRecordIds: string[];
}

export interface MacroCatalogBatch {
	id: string;
	fileName: string;
	sha256: string;
	sourceAsOfUtc?: string | null;
	committedAtUtc?: string | null;
	committedBy?: string | null;
	status: string;
	error?: string | null;
}

export interface MacroCatalogTotals {
	datasets: number;
	series: number;
	periods: number;
	observations: number;
}

export interface MacroCatalogDatasetItem {
	id: string;
	sector: string;
	sectorNameFr: string;
	nameFr: string;
	nameEn: string;
	icon: string;
	sortOrder: number;
	seriesCount: number;
	periodCount: number;
	observationCount: number;
	firstPeriod?: string | null;
	lastPeriod?: string | null;
	frequency: string;
	deltaObservations: number;
	lastPeriodAdvanced: boolean;
	/** Indicateurs publiés dont il manque l'unité ou le rattachement à un graphique. */
	seriesNeedingConfiguration?: number;
	missingUnits?: number;
	missingCharts?: number;
}

export interface MacroProductionStatus {
	upToDate: boolean;
	staleRecordIds: string[];
	/** Fiches de taux tirées du classeur (Bons BCC, interbancaire) pas encore en production. */
	pendingRateRecordIds?: string[];
	lastPromotedAtUtc?: string | null;
}

export interface MacroCatalogOverview {
	generatedAtUtc: string;
	activeBatch?: MacroCatalogBatch | null;
	previousBatch?: MacroCatalogBatch | null;
	totals: MacroCatalogTotals;
	datasets: MacroCatalogDatasetItem[];
	production: MacroProductionStatus;
}

export interface MacroProductionPublishItem {
	id: string;
	action: string;
	error?: string | null;
}

export interface MacroProductionPublishResult {
	total: number;
	changed: number;
	skipped: number;
	deployed: number;
	failed: number;
	items: MacroProductionPublishItem[];
}

const config = { withCredentials: true };

/** Authenticated API facade for the import-only macroeconomic workflow. */
export const macroDataClient = {
	listImports: async (): Promise<MacroImportHistoryItem[]> =>
		(await axios.get<MacroImportHistoryItem[]>(`${base}/imports`, config)).data ?? [],
	getCatalog: async (): Promise<MacroCatalogOverview> =>
		(await axios.get<MacroCatalogOverview>(`${base}/catalog`, config)).data,
	validate: async (file: File): Promise<MacroImportReport> => {
		const form = new FormData();
		form.append("file", file);
		return (
			await axios.post<MacroImportReport>(`${base}/imports/validate`, form, {
				...config,
				headers: { "Content-Type": "multipart/form-data" }
			})
		).data;
	},
	/**
	 * `allowDataLoss` : confirmation explicite du retrait de données déjà publiées.
	 * `acceptedLoss` : empreinte de la liste de retraits que l'opérateur a lue ; le serveur refuse
	 * (409, `listChanged`) si la base a changé depuis.
	 */
	commit: async (id: string, allowDataLoss = false, acceptedLoss?: string): Promise<MacroImportCommitResult> => {
		const query = new URLSearchParams();
		if (allowDataLoss) {
			query.set("allowDataLoss", "true");
			if (acceptedLoss) query.set("acceptedLoss", acceptedLoss);
		}
		const suffix = query.toString() ? `?${query.toString()}` : "";
		return (
			await axios.post<MacroImportCommitResult>(
				`${base}/imports/${encodeURIComponent(id)}/commit${suffix}`,
				null,
				config
			)
		).data;
	},
	/** Télécharge le classeur source exact d'un lot (audit). Passe par axios : un <a href> nu perd l'authentification. */
	downloadWorkbook: async (id: string, fileName: string): Promise<void> => {
		const response = await axios.get(`${base}/imports/${encodeURIComponent(id)}/workbook`, {
			...config,
			responseType: "blob"
		});
		const url = window.URL.createObjectURL(response.data as Blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = fileName;
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		window.URL.revokeObjectURL(url);
	},
	// L'API renvoie { published } : lire le booléen brut affichait toujours le toast succès.
	republishPreview: async (): Promise<boolean> =>
		(await axios.post<{ published: boolean }>(`${base}/publish-preview`, null, config)).data?.published ?? false,
	anomalies: async (includeResolved = false): Promise<MacroStructureAnomaly[]> =>
		(await axios.get<MacroStructureAnomaly[]>(
			`${base}/anomalies${includeResolved ? "?includeResolved=true" : ""}`, config)).data ?? [],
	adoptSheetName: async (sheetName: string, datasetId: string): Promise<void> => {
		await axios.post(`${base}/anomalies/adopt-sheet`, { sheetName, datasetId }, config);
	},
	publishProduction: async (): Promise<MacroProductionPublishResult> =>
		(await axios.post<MacroProductionPublishResult>(`${base}/publish-production`, null, config)).data
};

